import * as z from 'zod/v4';

import { BiQueryMode } from '@watershed/constants/bi';
import {
  LineageAssetKind,
  LineageDirection,
} from '@watershed/constants/lineage';

import { permissionReferenceSchema } from './tableReference';

export const snapshotQueryContextSchema = z.object({
  mode: z.literal(BiQueryMode.Snapshot),
  footprintSnapshotIds: z.tuple([z.string()], z.string()),
  // Should this be FootprintKind?
  footprintKind: z.string().nullish(),
  includeDedupedRows: z.boolean().nullish(),
});

/**
 * A snapshot query context, describing the context in which a BI snapshot query is being executed
 */
export type SnapshotQueryContext = z.infer<typeof snapshotQueryContextSchema>;

export const footprintExportQueryContextSchema =
  snapshotQueryContextSchema.extend({
    mode: z.literal(BiQueryMode.FootprintExport),
  });

/**
 * A footprint export query context, describing the context in which an export of a footprint
 * snapshot and potentially its related activity data is being executed
 */
export type FootprintExportQueryContext = z.infer<
  typeof footprintExportQueryContextSchema
>;

const financeSnapshotQueryContextSchema = z.object({
  mode: z.literal(BiQueryMode.FinanceFootprintSnapshot),
  footprintSnapshotId: z.string(),
  footprintKind: z.string().nullish(),
});

/**
 * A finance snapshot query context, describing the context in which a BI finance snapshot query is being executed
 */
export type FinanceSnapshotQueryContext = z.infer<
  typeof financeSnapshotQueryContextSchema
>;

const activityQueryContextSchema = z.object({
  mode: z.literal(BiQueryMode.Activity),
  businessActivityTypes: z.tuple([z.string()], z.string()).nullable(),
  measurementProjectIds: z.array(z.string()).nullish(),
  datasourceIds: z.array(z.string()).nullish(),
  consolidateExpenditureTypes: z.boolean().nullish(),
});

/**
 * An activity query context, describing the context in which a BI activity query is being executed
 */
export type ActivityQueryContext = z.infer<typeof activityQueryContextSchema>;

const lineageAssetPointerSchema = z.object({
  kind: z.nativeEnum(LineageAssetKind),
  parquetFileMetadataId: z.string().nullish(),
  dataRegistryTableName: z.string().nullish(),
  bartStableId: z.string().nullish(),
  extbartStableId: z.string().nullish(),
  fileMetadataId: z.string().nullish(),
  activityDatasetVersionId: z.string().nullish(),
});

const lineageQueryContextSchema = z.object({
  mode: z.literal(BiQueryMode.Lineage),
  permissionReference: permissionReferenceSchema,
  startAssetPointer: lineageAssetPointerSchema,
  endAssetPointer: lineageAssetPointerSchema.nullish(),
  direction: z.nativeEnum(LineageDirection),
});

/**
 * A lineage query context, describing the context in which a BI lineage query is being executed
 */
export type LineageQueryContext = z.infer<typeof lineageQueryContextSchema>;

const referenceDataQueryContextSchema = z.object({
  mode: z.literal(BiQueryMode.ReferenceData),
  referenceDataVersionId: z.string(),
});

/**
 * A reference data query context, describing the context in which a BI reference data query is being executed
 */
export type ReferenceDataQueryContext = z.infer<
  typeof referenceDataQueryContextSchema
>;

const materialMasterQueryContextSchema = z.object({
  mode: z.literal(BiQueryMode.MaterialMaster),
  tableName: z.string().nullish(),
  materialMethodologyVersionId: z.string().nullish(),
});

/**
 * A material master query context, describing the context in which a BI material master query is being executed
 */
export type MaterialMasterQueryContext = z.infer<
  typeof materialMasterQueryContextSchema
>;

const productFootprintQueryContextSchema = z.object({
  mode: z.literal(BiQueryMode.ProductFootprint),
  tableName: z.string().nullish(),
  materialMethodologyVersionId: z.string().nullish(),
});

/**
 * A product footprint query context, describing the context in which a BI product footprint query is being executed
 */
export type ProductFootprintQueryContext = z.infer<
  typeof productFootprintQueryContextSchema
>;

/**
 * An object describing the context in which a BI query is being executed
 */
export const queryContextSchema = z.discriminatedUnion('mode', [
  snapshotQueryContextSchema,
  footprintExportQueryContextSchema,
  financeSnapshotQueryContextSchema,
  activityQueryContextSchema,
  lineageQueryContextSchema,
  referenceDataQueryContextSchema,
  materialMasterQueryContextSchema,
  productFootprintQueryContextSchema,
]);

/**
 * A query context schema for use in AI chat agents.
 * This is a union because as of Zod v4.1 the generated JSON schema has
 * switched from "anyOf" to "oneOf" which seems to break tool calling.
 *
 * We're doing a short-term fork of the above and will fix in next few days (by Dec 22, 2025).
 */
export const queryContextSchemaForAIChat = z.union([
  snapshotQueryContextSchema,
  footprintExportQueryContextSchema,
  financeSnapshotQueryContextSchema,
  activityQueryContextSchema,
  lineageQueryContextSchema,
  referenceDataQueryContextSchema,
  materialMasterQueryContextSchema,
  productFootprintQueryContextSchema,
]);

/**
 * An object describing the context in which a BI query is being executed
 */
export type QueryContext = z.infer<typeof queryContextSchema> & {
  // This is to ensure that we don't accidentally use this type as GQBiQueryContextInput,
  // which will accept any input object with the right mode and no other required fields.
  readonly finance?: 'NOT_GQL_INPUT';
};
